import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Row, Col, Badge, Card, Alert } from 'react-bootstrap';
import axios from 'axios';

const ViewOndutyRequest = ({
    show,
    onHide,
    request,
    employeeName,
    employeeCode,
    canApprove,
    onStatusChange
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [actionLogs, setActionLogs] = useState([]);
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    const fetchActionLogs = useCallback(async () => {
        if (!request?.onduty_id) return;
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}onduty/get_onduty_logs.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: { onduty_id: request.onduty_id }
            });

            if (response.data.success) {
                setActionLogs(response.data.logs || []);
            }
        } catch (error) {
            console.error('Error fetching action logs:', error);
        }
    }, [API_BASE, request?.onduty_id]);

    // Fetch action logs when modal opens
    useEffect(() => {
        if (show) {
            fetchActionLogs();
        }
    }, [show, fetchActionLogs]);

    const handleStatusChange = async (status, rejectionReason = '') => {
        if (!status) return;

        const confirmMessage = status === 'Approved'
            ? 'Are you sure you want to approve this on-duty request?'
            : status === 'Rejected'
                ? 'Are you sure you want to reject this on-duty request?'
                : 'Are you sure you want to cancel this on-duty request?';

        if (!window.confirm(confirmMessage)) return;

        try {
            setLoading(true);
            setError('');

            const token = localStorage.getItem('authToken');
            const payload = {
                onduty_id: request.onduty_id,
                status: status
            };

            if (status === 'Rejected' && rejectionReason) {
                payload.rejection_reason = rejectionReason;
            }

            const response = await axios.post(`${API_BASE}onduty/update_onduty_status.php`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                alert(`On-duty request ${status.toLowerCase()} successfully`);
                onStatusChange();
                onHide();
            } else {
                setError(response.data.error || `Failed to ${status.toLowerCase()} on-duty request`);
            }
        } catch (error) {
            setError(error.response?.data?.error || `Failed to ${status.toLowerCase()} on-duty request`);
            console.error(`Error ${status.toLowerCase()}ing on-duty request:`, error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'Pending': { variant: 'warning', icon: 'bi-hourglass-split' },
            'Approved': { variant: 'success', icon: 'bi-check-circle' },
            'Rejected': { variant: 'danger', icon: 'bi-x-circle' },
            'Cancelled': { variant: 'secondary', icon: 'bi-slash-circle' }
        };

        const config = statusConfig[status] || { variant: 'secondary', icon: 'bi-question-circle' };

        return (
            <Badge bg={config.variant} className="d-inline-flex align-items-center">
                <i className={`bi ${config.icon} me-1`}></i>
                {status}
            </Badge>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const time = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
        return `${day}/${month}/${year} ${time}`;
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        const [hours, minutes] = timeString.split(':');
        const date = new Date();
        date.setHours(hours, minutes);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const getPeriodText = (period) => {
        return period === 'Morning' ? 'Morning (Half Day)' : 'Afternoon (Half Day)';
    };

    const getRequestTypeIcon = (type) => {
        switch(type) {
            case 'Full Day': return 'bi-sun';
            case 'Half Day': return 'bi-sun-half';
            case 'Specific Hours': return 'bi-clock';
            case 'Multiple Days': return 'bi-calendar-range';
            default: return 'bi-briefcase';
        }
    };

    if (!request) return null;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>
                    <i className="bi bi-briefcase me-2"></i>
                    On-Duty Request Details
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <div>{error}</div>
                    </Alert>
                )}

                {/* Employee Information */}
                <Card className="mb-4 border-0 shadow-sm">
                    <Card.Body>
                        <Row className="g-3">
                            <Col md={6}>
                                <div className="d-flex align-items-center">
                                    <div className="employee-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                        style={{ width: '50px', height: '50px' }}
                                    >
                                        <i className="bi bi-person fs-4"></i>
                                    </div>
                                    <div>
                                        <h5 className="mb-1 fw-bold">{employeeName}</h5>
                                        <p className="text-muted mb-0">
                                            <i className="bi bi-person-badge me-1"></i>
                                            {employeeCode} | ID: {request.emp_id}
                                        </p>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6} className="text-end">
                                <div className="d-flex flex-column align-items-end">
                                    <div className="mb-2">
                                        {getStatusBadge(request.status)}
                                    </div>
                                    <small className="text-muted">
                                        Request ID: <strong>OD-{request.onduty_id.toString().padStart(6, '0')}</strong>
                                    </small>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Request Type Badge */}
                <div className="mb-3">
                    <Badge bg="info" className="p-2">
                        <i className={`bi ${getRequestTypeIcon(request.request_type)} me-1`}></i>
                        {request.request_type} ({request.timing_mode})
                    </Badge>
                </div>

                {/* On-Duty Details */}
                <Card className="mb-4 border-0 shadow-sm">
                    <Card.Header className="bg-light">
                        <h6 className="mb-0 fw-bold">
                            <i className="bi bi-calendar3 me-2"></i>
                            On-Duty Details
                        </h6>
                    </Card.Header>
                    <Card.Body>
                        {request.timing_mode === 'Date Based' ? (
                            <Row className="g-3">
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted mb-1">
                                            <i className="bi bi-calendar-range me-1"></i>
                                            Date Range
                                        </label>
                                        <h6 className="mb-0 fw-bold">
                                            {formatDate(request.from_date)} - {formatDate(request.to_date)}
                                        </h6>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted mb-1">
                                            <i className="bi bi-clock me-1"></i>
                                            Total Duration
                                        </label>
                                        <h6 className="mb-0 fw-bold">
                                            {request.total_days} days
                                        </h6>
                                    </div>
                                </Col>

                                {Number(request.first_day_half) === 1 && (
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="form-label text-muted mb-1">
                                                <i className="bi bi-sun me-1"></i>
                                                First Day
                                            </label>
                                            <div>
                                                <Badge bg="info">
                                                    {getPeriodText(request.first_day_period)}
                                                </Badge>
                                            </div>
                                        </div>
                                    </Col>
                                )}

                                {Number(request.last_day_half) === 1 && (
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="form-label text-muted mb-1">
                                                <i className="bi bi-sunset me-1"></i>
                                                Last Day
                                            </label>
                                            <div>
                                                <Badge bg="info">
                                                    {getPeriodText(request.last_day_period)}
                                                </Badge>
                                            </div>
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        ) : (
                            <Row className="g-3">
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted mb-1">
                                            <i className="bi bi-clock-history me-1"></i>
                                            Date & Time Range
                                        </label>
                                        <h6 className="mb-0 fw-bold">
                                            {formatDateTime(request.from_datetime)} - {formatDateTime(request.to_datetime)}
                                        </h6>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted mb-1">
                                            <i className="bi bi-clock me-1"></i>
                                            Total Hours
                                        </label>
                                        <h6 className="mb-0 fw-bold">
                                            {request.total_hours} hours
                                        </h6>
                                    </div>
                                </Col>

                                {request.time_slot_type && (
                                    <Col md={12}>
                                        <div className="mb-3">
                                            <label className="form-label text-muted mb-1">
                                                <i className="bi bi-calendar-check me-1"></i>
                                                Time Slot
                                            </label>
                                            <div>
                                                <Badge bg="info">
                                                    {request.time_slot_type}
                                                    {request.time_slot_type === 'Custom' && request.custom_start_time && request.custom_end_time && (
                                                        <>: {formatTime(request.custom_start_time)} - {formatTime(request.custom_end_time)}</>
                                                    )}
                                                </Badge>
                                            </div>
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        )}

                        <Row className="mt-3">
                            <Col md={12}>
                                <div className="mb-3">
                                    <label className="form-label text-muted mb-1">
                                        <i className="bi bi-chat-left-text me-1"></i>
                                        Purpose
                                    </label>
                                    <Card className="bg-light">
                                        <Card.Body className="py-3">
                                            <p className="mb-0">{request.purpose || 'No purpose provided'}</p>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </Col>

                            {request.destination && (
                                <Col md={12}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted mb-1">
                                            <i className="bi bi-geo-alt me-1"></i>
                                            Destination
                                        </label>
                                        <Card className="bg-light">
                                            <Card.Body className="py-3">
                                                <p className="mb-0">{request.destination}</p>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </Card.Body>
                </Card>

                {/* Approval Info */}
                {request.approved_by && request.approved_at && (
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Header className="bg-light">
                            <h6 className="mb-0 fw-bold">
                                <i className="bi bi-check-circle me-2"></i>
                                Approval Information
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <small className="text-muted d-block">Approved By</small>
                                    <strong>User ID: {request.approved_by}</strong>
                                </Col>
                                <Col md={6}>
                                    <small className="text-muted d-block">Approved At</small>
                                    <strong>{formatDateTime(request.approved_at)}</strong>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                )}

                {/* Action History */}
                {actionLogs.length > 0 && (
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-light">
                            <h6 className="mb-0 fw-bold">
                                <i className="bi bi-list-check me-2"></i>
                                Action History
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <div className="timeline">
                                {actionLogs.map((log, index) => (
                                    <div key={log.log_id} className="timeline-item">
                                        <div className="timeline-marker"></div>
                                        <div className="timeline-content">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <h6 className="mb-0">
                                                    <Badge bg={
                                                        log.action === 'Approved' ? 'success' :
                                                            log.action === 'Rejected' ? 'danger' :
                                                                log.action === 'Cancelled' ? 'secondary' :
                                                                    'info'
                                                    }>
                                                        {log.action}
                                                    </Badge>
                                                </h6>
                                                <small className="text-muted">
                                                    {formatDateTime(log.created_at)}
                                                </small>
                                            </div>
                                            <small className="text-muted">
                                                Performed by User ID: {log.performed_by}
                                            </small>
                                            {log.details && (
                                                <div className="mt-2 p-2 bg-light rounded">
                                                    <small className="text-muted">{log.details}</small>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={loading}>
                    Close
                </Button>

                {request.status === 'Pending' && (
                    <>
                        {canApprove && (
                            <>
                                <Button
                                    variant="success"
                                    onClick={() => handleStatusChange('Approved')}
                                    disabled={loading}
                                    title="Approve"
                                    aria-label="Approve"
                                >
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                    ) : (
                                        <i className="bi bi-check-lg"></i>
                                    )}
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={() => {
                                        const reason = window.prompt('Enter rejection reason (optional):');
                                        handleStatusChange('Rejected', reason);
                                    }}
                                    disabled={loading}
                                    title="Reject"
                                    aria-label="Reject"
                                >
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                    ) : (
                                        <i className="bi bi-x-lg"></i>
                                    )}
                                </Button>
                            </>
                        )}
                        <Button
                            variant="warning"
                            onClick={() => {
                                const confirmed = window.confirm('Are you sure you want to cancel this on-duty request?');
                                if (confirmed) handleStatusChange('Cancelled');
                            }}
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            ) : (
                                <i className="bi bi-slash-circle me-2"></i>
                            )}
                            Cancel Request
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

// Add some CSS for the timeline
const timelineStyles = `
  .timeline {
    position: relative;
    padding-left: 20px;
  }
  
  .timeline::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: #dee2e6;
  }
  
  .timeline-item {
    position: relative;
    margin-bottom: 20px;
  }
  
  .timeline-marker {
    position: absolute;
    left: -20px;
    top: 0;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #6c757d;
    border: 2px solid white;
  }
  
  .timeline-content {
    margin-left: 10px;
  }
`;

// Add styles to document head
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = timelineStyles;
    document.head.appendChild(style);
}

export default ViewOndutyRequest;
